import React from 'react';
import { useLocation } from 'react-router';
import { Box, Grid } from '@mui/material';
import ShimmerBox from './ShimmerBox';
import TableShimmer from './TableShimmer';
import LogsCardsListShimmer from './LogsCardsListShimmer';
import DashboardShimmer from './DashboardShimmer';
import AutoProvisioningPageShimmer from './AutoProvisioningPageShimmer';
import ProjectCardShimmer from './ProjectCardShimmer';

export interface SnapshotPageShimmerProps {
  showSelection?: boolean;
}

/** Snapshot page layout: heading + table + reason card */
export const SnapshotPageShimmer: React.FC<SnapshotPageShimmerProps> = ({
  showSelection = true,
}) => (
  <Box
    data-testid="snapshot-page-shimmer"
    sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    }}
  >
    <ShimmerBox className="h-7 w-64 rounded-md" />
    <TableShimmer columns={4} rows={8} showSelection={showSelection} minHeight={360} />
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid var(--border-light)',
        bgcolor: 'var(--bg-primary)',
        p: 3,
      }}
    >
      <ShimmerBox className="mb-2 h-4 w-72 max-w-full rounded-md" />
      <ShimmerBox className="mb-4 h-3 w-full max-w-xl rounded" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ShimmerBox className="h-9 w-full max-w-[440px] rounded-xl" />
        <ShimmerBox className="ml-auto h-9 w-40 rounded-[10px]" />
      </Box>
    </Box>
  </Box>
);

export const LogsPageShimmer: React.FC<{ count?: number; withLeadingContent?: boolean }> = ({
  count = 3,
  withLeadingContent = false,
}) => (
  <Box
    data-testid="logs-page-shimmer"
    className="logs-page"
    sx={{
      width: '100%',
      maxWidth: 1200,
      mx: 'auto',
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    }}
  >
    <ShimmerBox className="mb-2 h-7 w-72 rounded-md" />
    <LogsCardsListShimmer count={count} withLeadingContent={withLeadingContent} />
  </Box>
);

const GenericPageShimmer: React.FC = () => (
  <Box
    data-testid="generic-page-shimmer"
    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
  >
    <ShimmerBox className="h-7 w-56 rounded-md" />
    <TableShimmer columns={5} rows={8} minHeight={360} />
  </Box>
);

const ProjectsPageShimmer: React.FC = () => (
  <Box data-testid="projects-page-shimmer" sx={{ p: 2, maxWidth: 1200, mx: 'auto', width: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
      <ShimmerBox className="h-7 w-64 rounded-md" />
      <ShimmerBox className="h-9 w-32 rounded-[10px]" />
    </Box>
    <ShimmerBox className="mb-3 h-16 w-full rounded-xl" />
    <Grid container spacing={1.25}>
      {Array.from({ length: 8 }, (_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={`project-route-shimmer-${index}`}>
          <ProjectCardShimmer />
        </Grid>
      ))}
    </Grid>
  </Box>
);

export type RouteShimmerVariant =
  | 'snapshot'
  | 'epms-logs'
  | 'falcon-logs'
  | 'logs-detail'
  | 'dashboard'
  | 'prohance'
  | 'manage-jobs'
  | 'falcon-configure'
  | 'projects'
  | 'generic';

export const resolveRouteShimmerVariant = (pathname: string): RouteShimmerVariant => {
  if (pathname.includes('/employee-master/logsDetail') || pathname.includes('/falcon/logsDetail')) {
    return 'logs-detail';
  }
  if (pathname.includes('/employee-master/logs')) return 'epms-logs';
  if (pathname.includes('/falcon/logs')) return 'falcon-logs';
  if (pathname.includes('/employee-master/dashboard')) return 'dashboard';
  if (pathname.includes('/employee-master/snapshot') || pathname === '/') return 'snapshot';
  if (pathname.includes('/auto-provisioning/manage-jobs')) return 'manage-jobs';
  if (pathname.includes('/auto-provisioning/configure')) return 'falcon-configure';
  if (pathname.includes('/auto-provisioning/prohance')) return 'prohance';
  if (pathname.includes('/projects/overview')) return 'projects';
  return 'generic';
};

const routeShellSx = {
  width: '100%',
  maxWidth: 1200,
  mx: 'auto',
  p: 2,
  minHeight: '50vh',
  bgcolor: 'transparent',
} as const;

/** Route-level shimmer matched to the page being lazy-loaded */
const RouteShimmer: React.FC = () => {
  const { pathname } = useLocation();
  const variant = resolveRouteShimmerVariant(pathname);

  return (
    <Box data-testid="route-shimmer" data-variant={variant} sx={routeShellSx}>
      {variant === 'snapshot' && <SnapshotPageShimmer />}
      {variant === 'epms-logs' && <LogsPageShimmer count={3} />}
      {variant === 'falcon-logs' && <LogsPageShimmer count={1} withLeadingContent />}
      {variant === 'logs-detail' && <GenericPageShimmer />}
      {variant === 'dashboard' && <DashboardShimmer />}
      {variant === 'prohance' && (
        <AutoProvisioningPageShimmer variant="prohance" tableColumns={6} />
      )}
      {variant === 'manage-jobs' && (
        <AutoProvisioningPageShimmer variant="manageJobs" tableColumns={8} />
      )}
      {variant === 'falcon-configure' && (
        <AutoProvisioningPageShimmer variant="falcon" tableColumns={7} />
      )}
      {variant === 'projects' && <ProjectsPageShimmer />}
      {variant === 'generic' && <GenericPageShimmer />}
    </Box>
  );
};

export default RouteShimmer;
